/**
 * Vista: Detalle de pedido para Recepción Visual
 *
 * Muestra un pedido con sus renglones y unidades.
 * Por cada unidad:
 *   - por_llegar → botón "Asignar serie" (abre modal)
 *   - recibido → botón "Hacer recepción visual" (abre flujo existente)
 *   - inspeccionado → botón "Ver"
 */

import recepcionService from '../services/recepcion.service.js';
import router from '../router/router.js';
import permisos from '../services/permissions.service.js';
import { normalizers, attachNormalizer } from '../utils/inputNormalizer.js';
import { subirVideoRecepcion } from '../utils/videoUploader.js';
import { fechaCorta } from '../utils/fechas.js';

export default {
  async render() {
    return `
      <div id="oc-detalle-container">
        <div style="text-align: center; padding: 3rem;">Cargando pedido...</div>
      </div>
    `;
  },

  async afterRender() {
    if (!permisos.canView('recepciones')) {
      router.navigate(permisos.primeraRuta || '/');
      return;
    }

    window.recepcionOCDetalleView = this;
    this.pedidoId = this._extraerIdDeRuta();

    if (!this.pedidoId) {
      this._renderError('ID de pedido no válido');
      return;
    }

    await this.cargarPedido();
  },

  _extraerIdDeRuta() {
    const path = window.location.pathname;
    const match = path.match(/\/almacen\/recepciones\/pedido\/(\d+)/);
    return match ? parseInt(match[1]) : null;
  },

  async cargarPedido() {
    try {
      this.pedido = await recepcionService.obtenerPedidoParaRecepcion(this.pedidoId);
      if (!this.pedido) {
        this._renderError('Pedido no encontrado');
        return;
      }
      this.filtros = this.filtros || { modelo: '', num_serie: '', estatus: '' };
      this._renderPedido();
    } catch (error) {
      console.error(error);
      this._renderError('Error al cargar el pedido');
    }
  },

  _renderError(mensaje) {
    const container = document.getElementById('oc-detalle-container');
    container.innerHTML = `
      <div class="card">
        <div style="text-align: center; padding: 3rem; color: var(--danger);">
          <h3>${mensaje}</h3>
          <button class="btn btn-secondary" onclick="router.navigate('/almacen/recepciones')" style="margin-top: 1rem;">
            ← Volver
          </button>
        </div>
      </div>
    `;
  },

  _renderPedido() {
    const p = this.pedido;
    const prog = p.progreso || {};
    // B5c: fecha_pedido es DATE ('YYYY-MM-DD'); con new Date() salía un día antes que en Compras
    const fechaEsp = p.fecha_entrega_estimada
      ? fechaCorta(p.fecha_entrega_estimada)
      : (p.fecha_pedido ? fechaCorta(p.fecha_pedido) : 'No definida');
    const porLlegar = (p.detalles || []).reduce((a, d) => a + d.unidades.filter((u) => u.estado === 'por_llegar').length, 0);
    const ligasCompras = [
      permisos.canView('ordenes_compra')
        ? `<button class="btn btn-sm btn-secondary" onclick="router.navigate('/compras/pedidos/${p.id}')">Ver pedido en Compras</button>`
        : '',
      permisos.canView('compras') && (prog.recibidos || 0) > 0
        ? `<button class="btn btn-sm btn-secondary" onclick="router.navigate('/compras/facturas?pedido=${p.id}')">🧾 Factura del proveedor</button>`
        : ''
    ].join(' ');

    const estadoBadge = {
      'completa': { cls: 'success', texto: 'Completa' },
      'en_recepcion': { cls: 'warning', texto: 'En recepción' },
      'pendiente': { cls: 'secondary', texto: 'Pendiente' },
      'sin_unidades': { cls: 'info', texto: 'Sin unidades' }
    }[prog.estado_recepcion] || { cls: 'secondary', texto: '-' };

    const container = document.getElementById('oc-detalle-container');
    container.innerHTML = `
      <div class="card">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 1.5rem; flex-wrap: wrap; gap: 1rem;">
          <div>
            <button class="btn btn-secondary" onclick="router.navigate('/almacen/recepciones')" style="margin-bottom: 0.75rem;">
              ← Volver
            </button>
            <h2 class="card-title" style="margin: 0;">📦 ${p.folio || 'Pedido'}</h2>
            <div style="color: var(--gray-500); font-size: 0.9rem; margin-top: 0.25rem;">
              ${p.proveedor_nombre || 'Sin proveedor'} · Fecha: ${fechaEsp}
            </div>
          </div>
          <div style="text-align: right;">
            <span class="badge badge-${estadoBadge.cls}" style="font-size: 1rem; padding: 0.5rem 1rem;">${estadoBadge.texto}</span>
            <div style="margin-top: 0.75rem; color: var(--gray-600); font-size: 0.9rem;">
              Recibidos: <strong>${prog.recibidos || 0}/${prog.total_unidades || 0}</strong> ·
              Por recibir: <strong>${porLlegar}</strong> ·
              Inspeccionados: <strong>${prog.inspeccionados || 0}/${prog.total_unidades || 0}</strong>
            </div>
            ${ligasCompras.trim() ? `<div style="margin-top: 0.6rem; display: flex; gap: 0.4rem; justify-content: flex-end; flex-wrap: wrap;">${ligasCompras}</div>` : ''}
          </div>
        </div>

        ${this._renderFiltrosHtml()}

        <div id="detalles-container"></div>
      </div>

      <!-- Modal Asignar Serie -->
      <div id="modal-asignar-serie" class="modal-overlay" style="display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 1000; justify-content: center; align-items: center;">
        <div style="background: white; border-radius: 8px; max-width: 500px; width: 90%; padding: 1.5rem;">
          <div id="modal-contexto" style="color: var(--primary); margin: 0 0 1.25rem; font-size: 1.15rem; font-weight: 600;"></div>

          <div class="form-group">
            <label class="form-label">Número de serie *</label>
            <input type="text" id="modal-serie" class="form-input" placeholder="Ej: 1JD2025001 o PRUEBA-EJ-01" />
            <small class="form-help">Tal cual viene en la placa: letras, números y guiones.</small>
          </div>

          <div class="form-group">
            <label class="form-label">Serie de motor *</label>
            <input type="text" id="modal-serie-motor" class="form-input" placeholder="Ej: MOT-2025-001" />
          </div>

          <div class="form-group">
            <label class="form-label">Notas (opcional)</label>
            <textarea id="modal-notas" class="form-input" rows="2" placeholder="Observaciones al recibir..."></textarea>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1rem; flex-wrap: wrap;">
            <button class="btn btn-secondary" onclick="window.recepcionOCDetalleView.cerrarModal()">Cancelar</button>
            <button class="btn btn-outline-primary" id="modal-guardar-btn" onclick="window.recepcionOCDetalleView.guardarSerie(false)">Guardar</button>
            <button class="btn btn-primary" id="modal-guardar-continuar-btn" onclick="window.recepcionOCDetalleView.guardarSerie(true)">Guardar y continuar a inspección</button>
          </div>
        </div>
      </div>
    `;

    this._attachFiltrosListeners();
    this._renderDetalles();
  },

  _renderFiltrosHtml() {
    const detalles = this.pedido.detalles || [];
    if (detalles.length === 0) return '';

    // Modelos únicos para el dropdown
    const modelosUnicos = [...new Set(detalles.map(d => `${d.marca}|${d.modelo}`))]
      .map(key => {
        const [marca, modelo] = key.split('|');
        return { key, label: `${marca} · ${modelo}` };
      });

    return `
      <div class="filters" style="display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; padding: 1rem; background: var(--gray-50); border-radius: 8px;">
        <div class="form-group" style="margin: 0;">
          <label class="form-label">Modelo</label>
          <select id="oc-filtro-modelo" class="form-select">
            <option value="">Todos</option>
            ${modelosUnicos.map(m => `<option value="${m.key}" ${this.filtros.modelo === m.key ? 'selected' : ''}>${m.label}</option>`).join('')}
          </select>
        </div>
        <div class="form-group" style="margin: 0;">
          <label class="form-label">Num. Serie</label>
          <input type="text" id="oc-filtro-serie" class="form-input" placeholder="Buscar serie..." value="${this.filtros.num_serie}" />
        </div>
        <div class="form-group" style="margin: 0;">
          <label class="form-label">Estatus</label>
          <select id="oc-filtro-estatus" class="form-select">
            <option value="">Todos</option>
            <option value="por_llegar" ${this.filtros.estatus === 'por_llegar' ? 'selected' : ''}>Por llegar</option>
            <option value="recibido" ${this.filtros.estatus === 'recibido' ? 'selected' : ''}>Recibido</option>
            <option value="inspeccionado" ${this.filtros.estatus === 'inspeccionado' ? 'selected' : ''}>Inspeccionado</option>
          </select>
        </div>
        <div class="form-group" style="margin: 0; display: flex; align-items: flex-end;">
          <button class="btn btn-secondary" onclick="window.recepcionOCDetalleView.limpiarFiltros()" style="width: 100%;">Limpiar</button>
        </div>
      </div>
    `;
  },

  _attachFiltrosListeners() {
    const selModelo = document.getElementById('oc-filtro-modelo');
    const inpSerie = document.getElementById('oc-filtro-serie');
    const selEstatus = document.getElementById('oc-filtro-estatus');
    if (!selModelo) return;

    selModelo.addEventListener('change', (e) => {
      this.filtros.modelo = e.target.value;
      this._renderDetalles();
    });
    selEstatus.addEventListener('change', (e) => {
      this.filtros.estatus = e.target.value;
      this._renderDetalles();
    });
    inpSerie.addEventListener('input', (e) => {
      clearTimeout(this._serieTimeout);
      this._serieTimeout = setTimeout(() => {
        this.filtros.num_serie = e.target.value.trim();
        this._renderDetalles();
      }, 250);
    });
  },

  limpiarFiltros() {
    this.filtros = { modelo: '', num_serie: '', estatus: '' };
    document.getElementById('oc-filtro-modelo').value = '';
    document.getElementById('oc-filtro-serie').value = '';
    document.getElementById('oc-filtro-estatus').value = '';
    this._renderDetalles();
  },

  _renderDetalles() {
    const container = document.getElementById('detalles-container');
    const detalles = this.pedido.detalles || [];

    if (detalles.length === 0) {
      container.innerHTML = `<div style="text-align: center; padding: 2rem; color: var(--gray-500);">Este pedido no tiene equipos.</div>`;
      return;
    }

    const detallesConFiltro = detalles
      .filter(d => {
        if (!this.filtros.modelo) return true;
        return `${d.marca}|${d.modelo}` === this.filtros.modelo;
      })
      .map(d => ({
        ...d,
        unidadesFiltradas: (d.unidades || [])
          .map((u, idx) => ({ ...u, _numero: idx + 1 }))
          .filter(u => {
            if (this.filtros.estatus && u.estado !== this.filtros.estatus) return false;
            if (this.filtros.num_serie) {
              const ns = (u.num_serie || '').toLowerCase();
              if (!ns.includes(this.filtros.num_serie.toLowerCase())) return false;
            }
            return true;
          })
      }))
      .filter(d => d.unidadesFiltradas.length > 0);

    if (detallesConFiltro.length === 0) {
      container.innerHTML = `<div style="text-align: center; padding: 2rem; color: var(--gray-500);">No hay unidades que coincidan con los filtros.</div>`;
      return;
    }

    container.innerHTML = detallesConFiltro.map(d => this._renderDetalleCard(d)).join('');
  },

  _renderDetalleCard(detalle) {
    const unidades = detalle.unidadesFiltradas || detalle.unidades || [];
    const totalOriginal = (detalle.unidades || []).length;
    const conteoTxt = unidades.length === totalOriginal
      ? `${totalOriginal} unidad${totalOriginal !== 1 ? 'es' : ''}`
      : `${unidades.length} de ${totalOriginal} unidades`;

    return `
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; margin-bottom: 1rem;">
        <div style="background: var(--gray-50); padding: 0.75rem 1rem; border-bottom: 1px solid var(--gray-200); display: flex; justify-content: space-between; align-items: center; border-radius: 8px 8px 0 0;">
          <div>
            <strong>${detalle.marca} · ${detalle.modelo}${detalle.anio ? ` · ${detalle.anio}` : ''}</strong>
          </div>
          <div style="color: var(--gray-600); font-size: 0.9rem;">
            ${conteoTxt}
          </div>
        </div>
        <div style="padding: 0;">
          <table class="table" style="margin: 0;">
            <thead>
              <tr>
                <th></th>
                <th style="width: 60px;">#</th>
                <th>Num. Serie</th>
                <th class="tbl-hide-mobile">Estado</th>
                <th class="tbl-hide-mobile">Acción</th>
              </tr>
            </thead>
            <tbody>
              ${unidades.map(u => this._renderUnidadRow(detalle, u, u._numero || 0)).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  },

  _renderUnidadRow(detalle, unidad, numero) {
    const estadoBadge = {
      'por_llegar': { cls: 'secondary', texto: 'Por llegar' },
      'recibido': { cls: 'info', texto: 'Recibido' },
      'inspeccionado': { cls: 'success', texto: 'Inspeccionado' },
      'cancelado': { cls: 'danger', texto: 'Cancelado' }
    }[unidad.estado] || { cls: 'secondary', texto: unidad.estado };

    let accion = '';
    if (unidad.estado === 'por_llegar') {
      accion = permisos.canCreate('recepciones')
        ? `<button class="btn btn-sm btn-primary" onclick="window.recepcionOCDetalleView.abrirModalSerie(${unidad.id}, '${detalle.marca}', '${detalle.modelo}', ${numero})">Asignar serie</button>`
        : '';
    } else if (unidad.estado === 'recibido') {
      accion = this._renderKebabRecibido(unidad);
    } else if (unidad.estado === 'inspeccionado' && unidad.recepcion_id) {
      accion = `<button class="btn btn-sm btn-secondary" onclick="router.navigate('/almacen/recepciones/${unidad.recepcion_id}')">Ver</button>`;
    }

    const estadoBadgeHtml = `<span class="badge badge-${estadoBadge.cls}">${estadoBadge.texto}</span>`;

    return `
      <tr>
        <td><button class="tbl-expand-btn" onclick="window.recepcionOCDetalleView.toggleUnidadRow(${unidad.id})">&#9654;</button></td>
        <td>${numero}</td>
        <td><strong>${unidad.num_serie || '-'}</strong></td>
        <td class="tbl-hide-mobile">${estadoBadgeHtml}</td>
        <td class="tbl-hide-mobile">${accion}</td>
      </tr>
      <tr class="tbl-detail-row" id="detail-unidad-${unidad.id}">
        <td colspan="5" class="tbl-detail-cell">
          <dl class="tbl-detail-grid">
            <dt>Estado</dt><dd>${estadoBadgeHtml}</dd>
          </dl>
          <div style="margin-top: var(--spacing-sm); display: flex; gap: var(--spacing-xs);">${accion}</div>
        </td>
      </tr>
    `;
  },

  _renderKebabRecibido(unidad) {
    const canCreate = permisos.canCreate('recepciones');
    const canEdit = permisos.canEdit('recepciones');
    const canDelete = permisos.canDelete('recepciones');
    // Permisos dedicados (más estrictos que `editar`):
    //   - corregir_serie → fix de typo (rename con cascada)
    //   - liberar_serie  → hard delete del tractor + soft delete recepción
    const canCorregirSerie = permisos.can('recepciones', 'corregir_serie');
    const canLiberarSerie = permisos.can('recepciones', 'liberar_serie');
    const canSubirVideo = permisos.can('recepciones', 'subir_video');
    const recepNoCompletada = unidad.recepcion_completada !== 1 && unidad.recepcion_completada !== true;

    // Gate común de estado: sin recepción O recepción en borrador (no completada
    // y sin AMBAS firmas). Una sola firma no bloquea; ambas firmas sí (estado
    // próximo a completar). Misma regla en backend.
    const gateEstadoOK = (
      !unidad.recepcion_id ||
      (
        unidad.recepcion_completada !== 1 && unidad.recepcion_completada !== true &&
        !(unidad.recepcion_tiene_firma_almacen && unidad.recepcion_tiene_firma_recibio)
      )
    );
    const puedeCorregir = canCorregirSerie && gateEstadoOK;
    const puedeLiberar = canLiberarSerie && gateEstadoOK;

    const itemStyle = "display: block; width: 100%; padding: 0.6rem 0.9rem; border: none; background: none; text-align: left; cursor: pointer; font-size: 0.875rem; color: var(--gray-800); white-space: nowrap; transition: background 0.12s ease;";
    const itemHover = "onmouseenter=\"this.style.background='var(--gray-100)'\" onmouseleave=\"this.style.background='transparent'\" onmousedown=\"this.style.background='var(--gray-200)'\" onmouseup=\"this.style.background='var(--gray-100)'\"";

    const items = [];

    if (unidad.recepcion_id) {
      // Continuar = abrir el detalle a editar → requiere permiso `editar`.
      if (canEdit) {
        items.push(`<button class="kebab-item" onclick="router.navigate('/almacen/recepciones/${unidad.recepcion_id}')" style="${itemStyle}" ${itemHover}>Continuar recepción</button>`);
      }
    } else if (canCreate) {
      items.push(`<button class="kebab-item" onclick="window.recepcionOCDetalleView.hacerRecepcion('${unidad.num_serie}')" style="${itemStyle}" ${itemHover}>Hacer recepción</button>`);
    }

    // Subir video directo (sin entrar al detalle), solo si la recepción ya
    // existe y no está completada. Permiso dedicado `subir_video`.
    if (unidad.recepcion_id && canSubirVideo && recepNoCompletada) {
      const tieneVideo = !!unidad.recepcion_tiene_video;
      items.push(`<button class="kebab-item" onclick="window.recepcionOCDetalleView.subirVideoUnidad(${unidad.recepcion_id}, '${unidad.num_serie}', ${tieneVideo})" style="${itemStyle}" ${itemHover}>${tieneVideo ? 'Reemplazar' : 'Subir'} video</button>`);
    }

    if (puedeCorregir) {
      items.push(`<button class="kebab-item" onclick="window.recepcionOCDetalleView.abrirModalCorregirNS(${unidad.id}, '${unidad.num_serie}')" style="${itemStyle}" ${itemHover}>Corregir número de serie</button>`);
    }

    if (puedeLiberar) {
      const tieneRecep = !!unidad.recepcion_id;
      items.push(`<button class="kebab-item" onclick="window.recepcionOCDetalleView.abrirModalLiberarNS(${unidad.id}, '${unidad.num_serie}', ${tieneRecep})" style="${itemStyle}" ${itemHover}>Liberar número de serie</button>`);
    }

    if (unidad.recepcion_id && canDelete) {
      items.push(`<button class="kebab-item" onclick="window.recepcionOCDetalleView.eliminarBorrador(${unidad.recepcion_id}, '${unidad.num_serie}')" style="${itemStyle}" ${itemHover}>Cancelar recepción</button>`);
    }

    if (items.length === 0) return '';

    return `
      <div class="kebab-menu" style="position: relative; display: inline-block;">
        <button class="btn btn-sm btn-secondary kebab-trigger" onclick="window.recepcionOCDetalleView.toggleKebab(${unidad.id})" style="padding: 0.25rem 0.5rem; font-weight: bold;">⋮</button>
        <div class="kebab-dropdown" data-kebab-id="${unidad.id}" style="display: none; position: absolute; right: 0; top: 100%; background: white; border: 1px solid var(--gray-200); border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.15); min-width: 200px; z-index: 100; margin-top: 4px;">
          ${items.join('')}
        </div>
      </div>
    `;
  },

  abrirModalLiberarNS(unidadId, numSerieActual, tieneRecepcion) {
    const advertencia = tieneRecepcion
      ? `<div style="margin-top: 0.75rem; padding: 0.75rem 0.9rem; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; color: #991b1b; font-size: 0.85rem;">
           <strong>⚠️ Atención:</strong> también se borrará la recepción en borrador (checklist, fotos y demás avances que tenga).
         </div>`
      : '';
    const html = `
      <p style="margin: 0 0 0.75rem;">Vas a liberar el número de serie:</p>
      <div style="padding: 0.75rem 0.9rem; background: var(--gray-50); border: 1px solid var(--gray-200); border-radius: 6px; font-family: monospace; font-weight: 600; text-align: center;">${numSerieActual}</div>
      <p style="margin: 0.75rem 0 0; color: var(--gray-700); font-size: 0.9rem;">El tractor se eliminará y el slot regresará a <strong>Por llegar</strong>. Después podrás asignar este NS al modelo correcto.</p>
      ${advertencia}
      <div id="liberar-ns-error" style="display: none; margin-top: 0.75rem; padding: 0.75rem 0.9rem; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; color: var(--danger); font-size: 0.85rem;"></div>
    `;
    modal.open(
      'Liberar número de serie',
      html,
      [
        { text: 'Cancelar', class: 'btn-secondary' },
        { text: 'Liberar', class: 'btn-danger', onClick: () => this._ejecutarLiberarNS(unidadId), closeOnClick: false }
      ]
    );
  },

  async _ejecutarLiberarNS(unidadId) {
    if (this._liberandoNS) return;
    this._liberandoNS = true;

    this._showProgress('Liberando…');

    try {
      await recepcionService.liberarSerieUnidad(unidadId);
      this._hideProgress();
      toast.success('NS liberado exitosamente');
      modal.close();
      await this.cargarPedido();
    } catch (error) {
      console.error(error);
      this._hideProgress();
      const msg = error.message || 'Error al liberar el NS';
      const errEl = document.getElementById('liberar-ns-error');
      if (errEl) {
        errEl.textContent = msg;
        errEl.style.display = 'block';
      } else {
        toast.error(msg);
      }
    } finally {
      this._liberandoNS = false;
    }
  },

  abrirModalCorregirNS(unidadId, numSerieActual) {
    this.unidadCorrigiendoId = unidadId;
    this.numSerieActualCorrigiendo = numSerieActual;
    this._renderPasoFormularioCorregirNS('');
  },

  _renderPasoFormularioCorregirNS(valorPrevio = '') {
    const html = `
      <div class="form-group" style="margin: 0 0 1rem;">
        <label class="form-label" style="text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.04em; color: var(--gray-600);">Número de serie actual</label>
        <div style="padding: 0.6rem 0.9rem; background: var(--gray-50); border: 1px solid var(--gray-200); border-radius: 6px; font-family: monospace; font-weight: 600;">${this.numSerieActualCorrigiendo}</div>
      </div>
      <div class="form-group" style="margin: 0;">
        <label class="form-label" style="text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.04em; color: var(--gray-600);">Nuevo número de serie *</label>
        <input type="text" id="corregir-ns-input" class="form-input" placeholder="Escribe el NS correcto" style="font-family: monospace;" value="${valorPrevio}" />
        <div id="corregir-ns-hint" style="color: var(--gray-500); font-size: 0.8rem; margin-top: 0.35rem;">Formato esperado: 17 caracteres alfanuméricos</div>
        <div id="corregir-ns-error" style="display: none; margin-top: 0.6rem; padding: 0.75rem 0.9rem; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; color: var(--danger);">
          <div style="display: flex; align-items: flex-start; gap: 0.5rem;">
            <span style="font-size: 1rem; line-height: 1.2;">⊘</span>
            <div style="flex: 1; min-width: 0;">
              <div id="corregir-ns-error-title" style="font-weight: 600; font-size: 0.9rem;"></div>
              <div id="corregir-ns-error-sub" style="font-size: 0.8rem; color: #991b1b; margin-top: 0.15rem; display: none;"></div>
            </div>
          </div>
        </div>
      </div>
    `;
    modal.open(
      'Corregir número de serie',
      html,
      [
        { text: 'Cancelar', class: 'btn-secondary' },
        { text: 'Guardar corrección', class: 'btn-primary', onClick: () => this.guardarCorregirNS(), closeOnClick: false }
      ]
    );

    setTimeout(() => {
      const inp = document.getElementById('corregir-ns-input');
      if (!inp) return;
      inp.focus();
      // Posicionar el cursor al final si había valor previo
      if (valorPrevio) inp.setSelectionRange(valorPrevio.length, valorPrevio.length);
      // Normalizar entrada (alfanum + uppercase, sin espacios) — útil con dictado por voz.
      attachNormalizer(inp, normalizers.alfanumUpper);
      inp.addEventListener('input', () => this._limpiarErrorCorregirNS());
    }, 100);
  },

  _renderPasoConfirmacionCorregirNS(numSerieNuevo) {
    const html = `
      <p style="margin: 0 0 1rem; color: var(--gray-700);">Vas a cambiar el número de serie:</p>
      <div style="display: flex; align-items: center; justify-content: center; gap: 1rem; padding: 1rem; background: var(--gray-50); border: 1px solid var(--gray-200); border-radius: 6px; font-family: monospace; font-weight: 600; flex-wrap: wrap;">
        <span style="color: var(--gray-600); text-decoration: line-through;">${this.numSerieActualCorrigiendo}</span>
        <span style="font-size: 1.2rem; color: var(--primary);">→</span>
        <span style="color: var(--primary);">${numSerieNuevo}</span>
      </div>
      <div style="margin-top: 1rem; padding: 0.75rem 0.9rem; background: #fefce8; border: 1px solid #fde68a; border-radius: 6px; color: #854d0e; font-size: 0.85rem;">
        Verifica que el nuevo número de serie sea correcto.
      </div>
      <div id="corregir-ns-error-conf" style="display: none; margin-top: 0.75rem; padding: 0.75rem 0.9rem; background: #fef2f2; border: 1px solid #fecaca; border-radius: 6px; color: var(--danger); font-size: 0.85rem;"></div>
    `;
    modal.open(
      'Confirmar corrección',
      html,
      [
        { text: 'Atrás', class: 'btn-secondary', onClick: () => this._renderPasoFormularioCorregirNS(numSerieNuevo), closeOnClick: false },
        { text: 'Confirmar cambio', class: 'btn-primary', onClick: () => this._ejecutarCorregirNS(numSerieNuevo), closeOnClick: false }
      ]
    );
  },

  async _ejecutarCorregirNS(numSerieNuevo) {
    if (this._corrigiendoNS) return;
    this._corrigiendoNS = true;

    this._showProgress('Aplicando corrección…');

    try {
      await recepcionService.corregirSerieUnidad(this.unidadCorrigiendoId, numSerieNuevo);
      this._hideProgress();
      toast.success('NS corregido exitosamente');
      modal.close();
      await this.cargarPedido();
    } catch (error) {
      console.error(error);
      this._hideProgress();
      const msg = error.message || 'Error al corregir el NS';
      // Si el error tiene que ver con "ya existe", regresamos al formulario para que pueda
      // corregir el NS (no tiene sentido confirmarlo otra vez con el mismo valor).
      if (/ya existe un tractor con NS|ya existe un tractor con numero|already exists/i.test(msg)) {
        this._renderPasoFormularioCorregirNS(numSerieNuevo);
        setTimeout(() => {
          this._mostrarErrorCorregirNS(
            'Este número de serie ya está registrado',
            'No se puede tener dos tractores con el mismo NS'
          );
        }, 120);
        return;
      }
      const errEl = document.getElementById('corregir-ns-error-conf');
      if (errEl) {
        errEl.textContent = msg;
        errEl.style.display = 'block';
      } else {
        toast.error(msg);
      }
    } finally {
      this._corrigiendoNS = false;
    }
  },

  _mostrarErrorCorregirNS(titulo, subtitulo) {
    const errEl = document.getElementById('corregir-ns-error');
    const titleEl = document.getElementById('corregir-ns-error-title');
    const subEl = document.getElementById('corregir-ns-error-sub');
    const inp = document.getElementById('corregir-ns-input');
    if (titleEl) titleEl.textContent = titulo;
    if (subEl) {
      if (subtitulo) {
        subEl.textContent = subtitulo;
        subEl.style.display = 'block';
      } else {
        subEl.textContent = '';
        subEl.style.display = 'none';
      }
    }
    if (errEl) errEl.style.display = 'block';
    if (inp) {
      inp.style.borderColor = 'var(--danger)';
      inp.focus();
    }
  },

  _limpiarErrorCorregirNS() {
    const errEl = document.getElementById('corregir-ns-error');
    const titleEl = document.getElementById('corregir-ns-error-title');
    const subEl = document.getElementById('corregir-ns-error-sub');
    const inp = document.getElementById('corregir-ns-input');
    if (titleEl) titleEl.textContent = '';
    if (subEl) { subEl.textContent = ''; subEl.style.display = 'none'; }
    if (errEl) errEl.style.display = 'none';
    if (inp) inp.style.borderColor = '';
  },

  guardarCorregirNS() {
    this._limpiarErrorCorregirNS();
    const inp = document.getElementById('corregir-ns-input');
    const nuevo = (inp?.value || '').trim();

    if (!nuevo) {
      this._mostrarErrorCorregirNS('El nuevo NS es requerido');
      return false;
    }
    if (nuevo === this.numSerieActualCorrigiendo) {
      this._mostrarErrorCorregirNS('El nuevo NS es igual al actual');
      return false;
    }

    // Validación pasada → mostrar paso de confirmación en el mismo modal
    this._renderPasoConfirmacionCorregirNS(nuevo);
  },

  toggleUnidadRow(id) {
    const detailRow = document.getElementById(`detail-unidad-${id}`);
    const btn = detailRow?.previousElementSibling?.querySelector('.tbl-expand-btn');
    if (detailRow) {
      detailRow.classList.toggle('open');
      btn?.classList.toggle('open');
    }
  },

  // ============================================================
  // Modal Asignar Serie
  // ============================================================

  abrirModalSerie(unidadId, marca, modelo, numero) {
    this.unidadActual = unidadId;

    document.getElementById('modal-contexto').textContent = `${marca} ${modelo} · Equipo ${numero}`;
    const inpSerie = document.getElementById('modal-serie');
    const inpSerieMotor = document.getElementById('modal-serie-motor');
    inpSerie.value = '';
    inpSerieMotor.value = '';
    document.getElementById('modal-notas').value = '';

    // Normalizar entrada en tiempo real: alfanuméricos + UPPERCASE, sin espacios.
    // Útil sobre todo cuando el usuario dicta por voz (que mete espacios y minúsculas).
    attachNormalizer(inpSerie, normalizers.alfanumUpper);
    attachNormalizer(inpSerieMotor, normalizers.alfanumUpper);

    const modal = document.getElementById('modal-asignar-serie');
    modal.style.display = 'flex';

    setTimeout(() => document.getElementById('modal-serie').focus(), 100);
  },

  cerrarModal() {
    document.getElementById('modal-asignar-serie').style.display = 'none';
    this.unidadActual = null;
  },

  async guardarSerie(continuarARecepcion = false) {
    const serie = document.getElementById('modal-serie').value.trim();
    const serieMotor = document.getElementById('modal-serie-motor').value.trim();
    const notas = document.getElementById('modal-notas').value.trim();

    if (!serie) {
      toast.error('El número de serie es requerido');
      document.getElementById('modal-serie').focus();
      return;
    }

    if (!serieMotor) {
      toast.error('La serie de motor es requerida');
      document.getElementById('modal-serie-motor').focus();
      return;
    }

    const btnGuardar = document.getElementById('modal-guardar-btn');
    const btnContinuar = document.getElementById('modal-guardar-continuar-btn');
    btnGuardar.disabled = true;
    btnContinuar.disabled = true;
    const btnActivo = continuarARecepcion ? btnContinuar : btnGuardar;
    const textoOriginal = btnActivo.textContent;
    btnActivo.textContent = 'Guardando...';

    try {
      await recepcionService.asignarSerieAUnidad(this.unidadActual, {
        num_serie: serie,
        serie_motor: serieMotor,
        notas: notas || null
      });

      toast.success('Serie asignada correctamente');
      this.cerrarModal();

      if (continuarARecepcion) {
        router.navigate(`/almacen/recepciones/nueva?num_serie=${encodeURIComponent(serie)}`);
      } else {
        await this.cargarPedido();
      }
    } catch (error) {
      console.error(error);
      const msg = error.response?.data?.message || error.message || 'Error al asignar serie';
      toast.error(msg);
    } finally {
      btnGuardar.disabled = false;
      btnContinuar.disabled = false;
      btnActivo.textContent = textoOriginal;
    }
  },

  hacerRecepcion(numSerie) {
    // Por ahora redirige al flujo actual pasando la serie por query
    router.navigate(`/almacen/recepciones/nueva?num_serie=${encodeURIComponent(numSerie)}`);
  },

  toggleKebab(unidadId) {
    const targets = document.querySelectorAll(`.kebab-dropdown[data-kebab-id="${unidadId}"]`);
    if (targets.length === 0) return;

    const isOpen = targets[0].style.display === 'block';

    // Cerrar todos los kebabs (incluidos los de esta unidad si están abiertos)
    document.querySelectorAll('.kebab-dropdown').forEach(d => {
      d.style.display = 'none';
    });

    if (!isOpen) {
      targets.forEach(d => { d.style.display = 'block'; });

      setTimeout(() => {
        const closeOnOutside = (e) => {
          if (!e.target.closest('.kebab-menu')) {
            targets.forEach(d => { d.style.display = 'none'; });
            document.removeEventListener('click', closeOnOutside);
          }
        };
        document.addEventListener('click', closeOnOutside);
      }, 0);
    }
  },

  async subirVideoUnidad(recepcionId, numSerie, existeVideo = false) {
    const ok = await subirVideoRecepcion(recepcionId, numSerie, {
      onProgress: (msg) => this._showProgress(msg),
      existeVideo
    });
    this._hideProgress();
    if (ok) await this.cargarPedido();
  },

  eliminarBorrador(recepcionId, numSerie) {
    modal.confirm(
      'Cancelar recepción',
      `¿Estás seguro? Tractor ${numSerie}`,
      async () => {
        try {
          await recepcionService.eliminar(recepcionId);
          toast.success('Recepción cancelada');
          await this.cargarPedido();
        } catch (error) {
          console.error(error);
          toast.error(error.response?.data?.message || 'Error al cancelar la recepción');
        }
      }
    );
  },

  // Overlay de progreso full-screen — mismo patrón que NuevaRecepcion.js
  _showProgress(message) {
    let overlay = document.getElementById('progressOverlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'progressOverlay';
      overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.92); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 9999;';
      overlay.innerHTML = `
        <div class="spinner" style="margin-bottom: 1rem;"></div>
        <p id="progressMessage" style="font-size: 1rem; font-weight: 600; color: var(--gray-700); text-align: center; padding: 0 1.5rem;"></p>
      `;
      document.body.appendChild(overlay);
    }
    document.getElementById('progressMessage').textContent = message;
  },

  _hideProgress() {
    const overlay = document.getElementById('progressOverlay');
    if (overlay) overlay.remove();
  }
};
